import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import type { BDFilterModalNestedOptionGroup } from '~/common/components/BDFilter/BDFilterModal';
import useStyles from '~/common/components/BDFilter/BDFilterModal.styles';

type BDFilterNestedOptionGroupProps<T> = {
  /** The nested options and the filter key they write to. */
  group: BDFilterModalNestedOptionGroup<T>;
  /** Values currently selected under the group's filter key. */
  selected: string[];
  /** Toggles one nested option under the group's filter key. */
  onToggleOption: (type: keyof T, value: string) => void;
  /** Replaces every value under the group's filter key. */
  onSetValues: (type: keyof T, values: string[]) => void;
};

/**
 * Renders a parent checkbox with its options indented beneath it. The parent is
 * checked when every option is selected and indeterminate when only some are.
 * Clicking it selects every option, or clears them all once all are selected.
 * @param group - The nested options and the filter key they write to.
 * @param selected - Values currently selected under that key.
 * @param onToggleOption - Called with the key and value of a clicked option.
 * @param onSetValues - Called with the key and its new values when the parent is clicked.
 */
const BDFilterNestedOptionGroup = <T,>({
  group,
  selected,
  onToggleOption,
  onSetValues,
}: BDFilterNestedOptionGroupProps<T>) => {
  const { classes } = useStyles();
  const values = group.options.map((option) => String(option.value));
  const selectedCount = values.filter((value) =>
    selected.includes(value)
  ).length;
  const isAllSelected = values.length > 0 && selectedCount === values.length;
  const isPartlySelected = selectedCount > 0 && !isAllSelected;
  const labelId = `filter-nested-label-${group.id}`;

  return (
    <>
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            color="primary"
            className={classes.checkbox}
            data-testid={`filter-nested-parent-${group.id}`}
            checked={isAllSelected}
            indeterminate={isPartlySelected}
            // MUI's `indeterminate` only changes the icon. The native property
            // is what assistive technology announces as "mixed".
            inputRef={(input: HTMLInputElement | null) => {
              if (input) input.indeterminate = isPartlySelected;
            }}
            onChange={() =>
              onSetValues(group.type, isAllSelected ? [] : values)
            }
          />
        }
        label={
          <Typography
            id={labelId}
            variant="small_body_regular"
            style={{ marginInlineStart: '8px' }}
          >
            {group.label}
          </Typography>
        }
      />
      <Box
        role="group"
        aria-labelledby={labelId}
        className={classes.nestedOptions}
      >
        {group.options.map((option) => (
          <FormControlLabel
            key={`${group.id}-${option.value}`}
            value={option.value}
            control={
              <Checkbox
                size="small"
                color="primary"
                className={classes.checkbox}
                data-testid={`filter-option-${group.id}-${String(option.value)}`}
                checked={selected.includes(String(option.value))}
                value={option.value}
                onChange={(event) =>
                  onToggleOption(group.type, event.target.value)
                }
              />
            }
            label={
              <Typography
                variant="small_body_regular"
                style={{ marginInlineStart: '8px' }}
              >
                {option.label}
              </Typography>
            }
          />
        ))}
      </Box>
    </>
  );
};

export default BDFilterNestedOptionGroup;
